import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchAllVendors, createVendor, updateVendor, deleteVendor } from '../api/thamanService';
import Loader from '../components/Loader';
import ErrorMessage from '../components/ErrorMessage';
import VendorModal from '../components/VendorModal'; // Import our upgraded modal

export default function VendorsPage() {
    const [vendors, setVendors] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingVendor, setEditingVendor] = useState(null);

    const getVendors = async () => {
        try {
            setError(null);
            setIsLoading(true);
            const data = await fetchAllVendors();
            setVendors(data);
        } catch (err) {
            setError('Failed to fetch vendors. Make sure the API server is running.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getVendors();
    }, []);

    const handleSaveVendor = async (vendorData) => {
        try {
            if (editingVendor) {
                await updateVendor(editingVendor.id, vendorData);
            } else {
                await createVendor(vendorData);
            }
            getVendors(); // Refresh the list
        } catch (err) {
            setError('Failed to save the vendor. Please try again.');
            throw err; // Re-throw to keep modal open on failure
        }
    };

    const handleDeleteVendor = async (vendorId) => {
        if (window.confirm('Are you sure you want to delete this vendor?')) {
            try {
                await deleteVendor(vendorId);
                getVendors(); // Refresh the list
            } catch (err) {
                setError('Failed to delete the vendor.');
            }
        }
    };

    const handleOpenModal = (vendor = null) => {
        setEditingVendor(vendor);
        setIsModalOpen(true);
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold">Vendors</h1>
                <button 
                    onClick={() => handleOpenModal()}
                    className="bg-blue-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-blue-700"
                >
                    + Add Vendor
                </button>
            </div>

            {isLoading && <Loader message="Fetching vendors..." />}
            {error && <ErrorMessage message={error} />}
            
            {!isLoading && !error && (
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <ul className="divide-y divide-gray-200">
                        {vendors.length > 0 ? (
                            vendors.map(vendor => (
                                <li key={vendor.id} className="py-4 flex items-center">
                                    <Link to={`/vendors/${vendor.id}/offers`} className="flex-grow">
                                        <div className="hover:bg-gray-50 p-2 rounded-md">
                                            <p className="text-lg font-medium text-gray-900">{vendor.english_name}</p>
                                            <p className="text-sm text-gray-500">{vendor.arabic_name}</p>
                                        </div>
                                    </Link>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm text-gray-500 mr-4">{vendor.offers_count || 0} Offers</span>
                                        <button onClick={() => handleOpenModal(vendor)} className="text-blue-600 hover:text-blue-800">Edit</button>
                                        <button onClick={() => handleDeleteVendor(vendor.id)} className="text-red-600 hover:text-red-800">Delete</button>
                                    </div>
                                </li>
                            ))
                        ) : (
                            <p className="text-center text-gray-500 py-4">No vendors found. Add one to get started!</p>
                        )}
                    </ul>
                </div>
            )}

            <VendorModal 
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={handleSaveVendor}
                initialData={editingVendor}
            />
        </div>
    );
}